import React from "react";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";

import DualImageBanner from "../../Layout/DualImageBanner";
import ImageCollection from "../../Layout/ImageCollection";
import GridCollection from "../../Layout/GridCollection";
import ProductCollection from "../../Layout/ProductCollection";
import DualImageGrid from "../../Layout/DualImageGrid";
import Banner from "../../Layout/Banner";
import Testimonial from "../../Layout/Testimonial";
import Highlights from "../../Layout/Highlights";
import FooterLayout from "../../Layout/Footer";
import SplashScreen from "../../Layout/SplashScreen";
import { getMessage } from "../../../lib/translator";
import API from "../../../lib/api";
import {
  BannerWithMultipleButtons,
  BannerWithText,
  VideoCardCarousel,
} from "zopsmart-ui";
import Carousel from "../../Layout/Carousel";
import AppointmentForm from "../../Layout/AppointmentForm";
import ImageSlideshow from "../../Layout/ImageSlideshow";
import ImageWithText from "../../Layout/ImageWithText";
import RichText from "../../Layout/RichText";
import Image from "../../Image";

// Support only for Fashion Trends theme as of now
export const dualImageLayouts = ["DualImageBanner", "DualImage"];

// Layout components mapping with their names
export const availableComponents = {
  Images: {
    ImageSlideShow: ImageSlideshow,
    ImageWithText: ImageWithText,
    DualImageBanner: DualImageBanner,
    ImageCollection: ImageCollection,
    GridCollection: GridCollection,
  },
  "E-commerce": {
    ProductCollection: ProductCollection,
    CategoryCollection: ProductCollection,
    BrandCollection: ProductCollection,
    SearchResults: ProductCollection,
    DualImage: DualImageGrid,
  },
  Banners: {
    MarketingBanners: Banner,
    BannerWithText: BannerWithText,
    BannerWithButton: BannerWithText,
    BannerWithMultipleButtons: BannerWithMultipleButtons,
  },
  Videos: {
    VideoCardCarousel: VideoCardCarousel,
  },
  Forms: {
    AppointmentForm: AppointmentForm,
  },
  Text: {
    RichText: RichText,
  },
  Carousel: {
    Carousel: Carousel,
    Testimonial: Testimonial,
  },
  Blog: {
    BlogCollection: ProductCollection,
  },
  Seller: {
    SellerCollection: ProductCollection,
  },
  FooterLayout: {
    FooterLayout: FooterLayout,
  },
  Splash: {
    Splash: SplashScreen,
  },
  Influencer: {
    InfluencerCollection: ProductCollection,
  },
  Features: {
    Highlights: Highlights,
  },
  ImageLayout: {
    Image: Image,
  },
};

const DragHandle = SortableHandle((props) => (
  <span className="sortable-handle">{props.children}</span>
));

const SortableItem = SortableElement((props) => {
  const { value, layoutIndex, onEditClick, onRemoveClick } = props;
  let LayoutComponent = "";
  if (value.name === "ContentBlock") {
    LayoutComponent = availableComponents?.Text?.RichText;
  } else if (value.name === "Image") {
    LayoutComponent = availableComponents?.ImageLayout?.Image;
  }
  Object.keys(availableComponents).forEach(function (component, index) {
    if (availableComponents[component][value.name]) {
      LayoutComponent = availableComponents[component][value.name];
    }
  });
  let element = "";
  const isShowLayout = typeof LayoutComponent !== "string";
  return (
    <div className="layoutItem">
      {isShowLayout && (
        <>
          <h3>
            <DragHandle>
              {value.name.replace(/([A-Z])/g, " $1").trim()}
            </DragHandle>
          </h3>
          <div
            className={value.name + " editable layout"}
            ref={(el) => {
              element = el;
            }}
          >
            <LayoutComponent data={value.data} />
            <div className="toolbar">
              {!["MarketingBanners"].includes(value.name) && (
                <button
                  className="primary button"
                  onClick={(e) => {
                    onEditClick(layoutIndex, element.offsetTop);
                  }}
                >
                  Edit
                </button>
              )}
              <button
                className="button"
                onClick={(e) => {
                  e.preventDefault();
                  onRemoveClick(layoutIndex);
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export const SortableList = SortableContainer((props) => {
  return (
    <div className="layoutsContainer">
      {Array.isArray(props.items) &&
        props.items.map((value, index) => (
          <SortableItem
            key={`item-${index}`}
            index={index}
            value={value}
            layoutIndex={index} // for some reason index was not being sent as props
            {...props}
          />
        ))}
    </div>
  );
});

export const formatResponse = (response, page) => {
  let newResponse = [...response];

  newResponse.forEach((layout) => {
    if (layout.name === "ContentBlock" && layout?.data?.text) {
      layout.data.description = layout?.data?.text;
      delete layout?.data?.text;
    }
    if (layout.name === "Image" && layout?.data?.text) {
      layout.data.imageUrl = layout?.data?.url;
      delete layout?.data?.url;
    }
    if (
      layout.name === "CategoryCollection" ||
      layout.name === "BrandCollection"
    ) {
      let key = layout.name === "CategoryCollection" ? "category" : "brand";
      let collection = layout.value?.collection || [];
      collection = collection.map((val) => val[key]);
      layout.data[key] = collection;
      delete layout.value?.collection;
    }
    if (
      layout.name === "CategoryCollection" &&
      layout.data?.subcategories &&
      page === "category"
    ) {
      layout.name = "SubCategoryCollection";
    }
  });

  return newResponse;
};

export const getSelectOptionsData = async (
  selectField,
  dataFor,
  isDataLoaded,
  setDataCallback
) => {
  if (isDataLoaded) return;

  const api = new API({ url: `/catalogue-service/${selectField}` });
  try {
    const response = await api.get();
    if (response.status === "ERROR") {
      console.error(`Error fetching ${dataFor}`);
      return;
    }

    const options =
      response?.data[selectField]?.map((field) => ({
        text: field.name,
        value: field.slug,
      })) || [];

    options.push(
      { text: `All ${dataFor}`, value: "ALL" },
      { text: `Current ${selectField}`, value: "CURRENT" }
    );

    setDataCallback(dataFor, options);
  } catch (error) {
    console.error("Error fetching select options data:", error);
  }
};

export const saveLayouts = (layouts, index, formData = null) => {
  let updatedLayouts = [...layouts];

  if (!formData) {
    updatedLayouts =
      index !== undefined ? layouts.filter((_, idx) => idx !== index) : layouts;
  } else {
    updatedLayouts = updatedLayouts.filter((_, i) => i !== index);
    const layoutName = formData?.name;
    delete formData.name;
    updatedLayouts.splice(index, 0, {
      name: layoutName,
      data: { ...formData },
    });
  }

  if (!index && index !== 0) {
    updatedLayouts = layouts;
  }

  updatedLayouts.forEach((layout, i) => {
    const layoutName = layout.name;

    if (layoutName === "SubCategoryCollection") {
      layout.name = "CategoryCollection";
      layout.data.subcategories = true;
    }

    if (layoutName === "CategoryCollection" && formData && i === index) {
      layout.data.categoryIds = layout.data.category
        ?.map((category) => category.id)
        .filter(Boolean);
      delete layout.data.category;
    }

    if (layoutName === "BlogCollection" && formData && i === index) {
      layout.data.blogIds = layout.data.blog
        ?.map((blog) => blog.id)
        .filter(Boolean);
      delete layout.data.blog;
    }

    if (layoutName === "BrandCollection") {
      if (formData && i === index) {
        layout.data.brandIds = layout.data.brand
          ?.map((brand) => brand.id)
          .filter(Boolean);
      }
      delete layout.data.brand;
    }

    if (layoutName === "ProductCollection") {
      delete layout.data?.collection;
      ["hasImage", "hasStock", "hasOffers"].forEach((key) => {
        if (layout.data[key] === -1) layout.data[key] = "";
      });

      ["category", "brand", "tag"].forEach((key) => {
        if (layout.data[key] && typeof layout.data[key] === "object") {
          layout.data[key] = layout.data[key].slug;
        }
      });
    }

    if (
      [
        "ImageSlideShow",
        "ImageCollection",
        "GridCollection",
        "VideoCardCarousel",
        "Marquee",
        "HotSpot",
      ].includes(layout.name)
    ) {
      delete layout.data?.activeIndex;
    }

    delete layout.value;
    delete layout.data?.collection;
  });

  return updatedLayouts;
};

export const LayoutSelectionList = ({
  availableComponents,
  hideLayouts,
  currentComponent,
  getRespectiveComponents,
}) => {
  return (
    <ul>
      {Object.keys(availableComponents).map((component, index) => {
        if (hideLayouts.includes(component)) {
          return null;
        }
        return (
          <li
            key={`change-components-${index}`}
            className={currentComponent === component ? "active-layouts" : ""}
            onClick={() => getRespectiveComponents(component)}
          >
            {component.replace(/([A-Z])/g, " $1").trim()}
          </li>
        );
      })}
    </ul>
  );
};

export const LayoutOptions = ({
  availableComponents,
  currentComponent,
  dualImageLayouts,
  theme,
  pages,
  page,
  addLayout,
}) => {
  if (!availableComponents[currentComponent]) return null;

  return (
    <div className="ui-components">
      {Object.keys(availableComponents[currentComponent])
        .filter(
          (layout) =>
            !(theme?.name !== "Trends" && dualImageLayouts.includes(layout))
        )
        .map((layout, index) => {
          const Component = availableComponents[currentComponent][layout];
          let data = {};

          if (layout === "BannerWithButton") {
            data.button = true;
          }

          let categoryAndBrandCollection = layout === "SubCategoryCollection";

          return (
            <div key={`add-layout-${index}`}>
              <h3>
                {layout.replace(/([A-Z])/g, " $1").trim()}
                {categoryAndBrandCollection && <small>Beta</small>}
              </h3>
              <div className="layout-option">
                <Component data={data} page={pages[page]} />
                <div className="toolbar">
                  <button
                    className="primary button"
                    onClick={() => addLayout(layout, data)}
                  >
                    {getMessage("themes.selection.add")}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};
